import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
import { _axios } from '../../utils/axios';

const CreateMultiyatraBelt = ({ show, onHide }) => {
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        status: 'Active',
        blessing_text: '',
        image: null,
        action_button_label: '',
        priority: '',
        action_link: '',
        display_start_date: '',
        display_end_date: '',
        display_time: '',
        frequency: '',
        image_only: 0
    });

    // NEW: Language selection
    const [selectedLanguages, setSelectedLanguages] = useState({
        english: true,
        hindi: false,
        marathi: false,
        gujarati: false
    });

    // NEW: Display sections
    const [displaySections, setDisplaySections] = useState({
        home: false,
        wisdom: false,
        parikrama: false,
        teertha: false,
        devotion: false
    });

    // NEW: Language-specific data
    const [languageData, setLanguageData] = useState({
        english: { title: '', blessing_text: '', image: null, imagePreview: null },
        hindi: { title: '', blessing_text: '', image: null, imagePreview: null },
        marathi: { title: '', blessing_text: '', image: null, imagePreview: null },
        gujarati: { title: '', blessing_text: '', image: null, imagePreview: null }
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const emojiPattern = /[\p{Emoji}]/u;

    // NEW: Handle language checkbox changes
    const handleLanguageChange = (lang) => {
        setSelectedLanguages(prev => ({
            ...prev,
            [lang]: !prev[lang]
        }));
    };

    // NEW: Handle display section checkbox changes
    const handleDisplaySectionChange = (section) => {
        setDisplaySections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // NEW: Handle language-specific input changes
    const handleLanguageInputChange = (lang, field, value) => {
        if ((field === 'title' || field === 'blessing_text')) {
            if (emojiPattern.test(value) && !emojiPattern.test(languageData[lang][field])) {
                toast.error('Emojis are not allowed in text fields');
                return;
            }
        }

        setLanguageData(prev => ({
            ...prev,
            [lang]: {
                ...prev[lang],
                [field]: value
            }
        }));
    };

    // NEW: Handle language-specific image upload
    const handleLanguageImageChange = (lang, e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 3 * 1024 * 1024) {
                toast.error('Image size should be less than 3MB');
                return;
            }

            const fileType = file.type.toLowerCase();
            if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png') && !fileType.includes('webp')) {
                toast.error('Only JPG/PNG/WEBP files are allowed');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setLanguageData(prev => ({
                    ...prev,
                    [lang]: {
                        ...prev[lang],
                        image: file,
                        imagePreview: reader.result
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, checked } = e.target;
        if ((name === 'title' || name === 'blessing_text')) {
            if (emojiPattern.test(value) && !emojiPattern.test(formData[name])) {
                toast.error('Emojis are not allowed in text fields');
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'blessing_text') {
            setCharCount(value.length);
        }
        if (name === 'category' && value === 'Live Aarti') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                display_time: new Date().toTimeString().slice(0, 5)
            }));
        } else if (name === 'category' && value !== 'Live Aarti') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                display_time: ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: name === 'image_only' ? (checked ? 1 : 0) : value
            }));
        }
        if (name === 'display_start_date' || name === 'display_end_date') {
            const startDate = name === 'display_start_date' ? value : formData.display_start_date;
            const endDate = name === 'display_end_date' ? value : formData.display_end_date;

            if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);

                if (end < start) {
                    toast.error('End date cannot be earlier than start date');
                    return;
                }
            }
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 3 * 1024 * 1024) {
                toast.error('Image size should be less than 3MB');
                return;
            }

            const fileType = file.type.toLowerCase();
            if (!fileType.includes('jpeg') && !fileType.includes('jpg') && !fileType.includes('png') && !fileType.includes('webp')) {
                toast.error('Only JPG/PNG/WEBP files are allowed');
                return;
            }

            setFormData(prev => ({
                ...prev,
                image: file
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || isSubmitted) return;

        try {
            setIsSubmitting(true);

            // 1. Common Required Field Validations (Same as old code)
            const requiredFields = [
                { name: 'category', label: 'Category' },
                { name: 'priority', label: 'Priority' },
                { name: 'display_start_date', label: 'Display Start Date' },
                { name: 'frequency', label: 'Frequency' },
                { name: 'action_link', label: 'Action link/URL' }
            ];

            for (const field of requiredFields) {
                if (!formData[field.name]) {
                    toast.error(`Please enter ${field.label}`);
                    setIsSubmitting(false);
                    return;
                }
            }

            // 2. Section Validation (New: Ensure at least one section is selected)
            const selectedSections = Object.keys(displaySections).filter(key => displaySections[key]);
            if (selectedSections.length === 0) {
                toast.error('Please select at least one Display Section');
                setIsSubmitting(false);
                return;
            }

            // 3. Multilingual Validation (Validates only if the language is checked/selected)
            // Adjust 'selectedLanguages' to match whatever state you use to toggle the checkmarks
            const languagesToValidate = ['english', 'hindi', 'marathi', 'gujarati'];
            let hasAtLeastOneLang = false;

            for (const lang of languagesToValidate) {
                if (selectedLanguages[lang]) {
                    hasAtLeastOneLang = true;
                    const data = languageData[lang];

                    // Validate Title & Blessing Text if "Image Only" is NOT checked
                    if (formData.image_only === 0) {
                        if (!data.title || data.title.trim() === '') {
                            toast.error(`Title is required for ${lang}`);
                            setIsSubmitting(false);
                            return;
                        }
                        if (data.title.length > 30) {
                            toast.error(`${lang} Title must be 30 characters or less`);
                            setIsSubmitting(false);
                            return;
                        }
                        if (!data.blessing_text || data.blessing_text.trim() === '') {
                            toast.error(`Blessing text is required for ${lang}`);
                            setIsSubmitting(false);
                            return;
                        }
                        if (data.blessing_text.length > 50) {
                            toast.error(`${lang} Blessing Text must be 50 characters or less`);
                            setIsSubmitting(false);
                            return;
                        }
                    }

                    // Image validation per language
                    if (!data.image) {
                        toast.error(`Please upload an image for ${lang}`);
                        setIsSubmitting(false);
                        return;
                    }
                }
            }

            if (!hasAtLeastOneLang) {
                toast.error('Please select at least one language');
                setIsSubmitting(false);
                return;
            }

            // 4. Scheduling Validations (Same as old code)
            if (formData.category === 'Live Aarti' && !formData.display_time) {
                toast.error('Time is required for Live Aarti category');
                setIsSubmitting(false);
                return;
            }

            if (formData.display_start_date && formData.display_end_date) {
                if (new Date(formData.display_end_date) < new Date(formData.display_start_date)) {
                    toast.error('End date cannot be earlier than start date');
                    setIsSubmitting(false);
                    return;
                }
            }

            // 5. Prepare Payload
            const finalPayload = {
                ...formData,
                category: formData.category,
                status: formData.status,
                display_section: JSON.stringify(displaySections), // New Column

                // Map Language Data to the single-row columns
                title: languageData.english.title,
                blessing_text: languageData.english.blessing_text,
                action_button_label: formData.action_button_label || null,
                priority: formData.priority,
                action_link: formData.action_link || null,
                display_start_date: formData.display_start_date,
                display_end_date: formData.display_end_date || null,
                display_time: formData.display_time || null,
                frequency: formData.frequency,
                image_only: formData.image_only,
                title_hin: languageData.hindi.title,
                blessing_text_hin: languageData.hindi.blessing_text,

                title_mar: languageData.marathi.title,
                blessing_text_mar: languageData.marathi.blessing_text,

                title_guj: languageData.gujarati.title,
                blessing_text_guj: languageData.gujarati.blessing_text,

                thumbnailType: languageData.english.image?.type || 'image/jpeg'
            };

            // 6. Convert Images to Base64
            const toBase64 = file => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = error => reject(error);
            });

            if (languageData.english.image) finalPayload.thumbnailContent = await toBase64(languageData.english.image);
            if (languageData.hindi.image) finalPayload.thumbnailContent_hin = await toBase64(languageData.hindi.image);
            if (languageData.marathi.image) finalPayload.thumbnailContent_mar = await toBase64(languageData.marathi.image);
            if (languageData.gujarati.image) finalPayload.thumbnailContent_guj = await toBase64(languageData.gujarati.image);

            // 7. API Call
            const response = await _axios.post(`${ServerPath}/kuber-belt`, finalPayload);

            if (response.data.success) {
                setIsSubmitted(true);
                toast.success('Template created successfully');
                setTimeout(() => {
                    onHide();
                    window.location.reload();
                }, 1500);
            }

        } catch (error) {
            console.error('Submission error:', error);
            toast.error(error.response?.data?.message || 'An error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            category: '',
            status: 'Active',
            blessing_text: '',
            image: null,
            action_button_label: '',
            priority: '',
            action_link: '',
            display_start_date: '',
            display_end_date: '',
            display_time: '',
            frequency: '',
            image_only: 0
        });
        setSelectedLanguages({
            english: true,
            hindi: false,
            marathi: false,
            gujarati: false
        });
        setDisplaySections({
            home: false,
            wisdom: false,
            parikrama: false,
            teertha: false,
            devotion: false
        });
        setLanguageData({
            english: { title: '', blessing_text: '', image: null, imagePreview: null },
            hindi: { title: '', blessing_text: '', image: null, imagePreview: null },
            marathi: { title: '', blessing_text: '', image: null, imagePreview: null },
            gujarati: { title: '', blessing_text: '', image: null, imagePreview: null }
        });
        setImagePreview(null);
        setIsSubmitting(false);
        setIsSubmitted(false);
        setCharCount(0);
        onHide();
    };

    const categoryOptions = [
        'Daily Blessing',
        'Festival',
        'Quote',
        'Promotion',
        'Live Aarti',
        'Ad'
    ];

    const statusOptions = [
        'Active',
        'Archive'
    ];

    const frequencyOptions = [
        'Once',
        'Daily',
        'Weekly',
        'Monthly',
        'Quarterly',
        'Half-Yearly',
        'Annually'
    ];

    const actionButtonOptions = [
        'Click Here',
        'Know More',
        'Read More',
        'Watch Now',
        'Live Darshan',
        'See Details',
        'Learn More',
        'Buy Now',
        'Donate Now',
        'Sponsor Pooja',
        'Talk to Guru',
        'Take Course',
        'Join Live',
        'Plan Your Trip'
    ];

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            centered
            style={{ borderRadius: '10px' }}
        >
            <div style={{ borderRadius: '10px' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0', padding: '20px 24px' }}>
                    <Modal.Title style={{ fontWeight: '600', fontSize: '22px' }}>Create Templates</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: '36px' }}>
                    <Form>
                        {/* Image Only Checkbox */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="hasContainer"
                                    label="Image Only"
                                    name="image_only"
                                    checked={formData.image_only === 1 ? true : false}
                                    onChange={handleInputChange}
                                    style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#4A5568'
                                    }}
                                />
                            </Col>
                        </Row>

                        {/* All Titles in One Row */}
                        {formData.image_only === 0 && (
                            <Row className="mb-3">
                                {selectedLanguages.english && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                English Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.english.title}
                                                onChange={(e) => handleLanguageInputChange('english', 'title', e.target.value)}
                                                placeholder="English title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.english.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.hindi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Hindi Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.hindi.title}
                                                onChange={(e) => handleLanguageInputChange('hindi', 'title', e.target.value)}
                                                placeholder="Hindi title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.hindi.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.marathi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Marathi Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.marathi.title}
                                                onChange={(e) => handleLanguageInputChange('marathi', 'title', e.target.value)}
                                                placeholder="Marathi title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.marathi.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.gujarati && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Gujarati Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={languageData.gujarati.title}
                                                onChange={(e) => handleLanguageInputChange('gujarati', 'title', e.target.value)}
                                                placeholder="Gujarati title"
                                                maxLength={30}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.gujarati.title.length}/30
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* All Blessing Texts in One Row */}
                        {formData.image_only === 0 && (
                            <Row className="mb-3">
                                {selectedLanguages.english && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                English Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.english.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('english', 'blessing_text', e.target.value)}
                                                placeholder="English blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.english.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.hindi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Hindi Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.hindi.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('hindi', 'blessing_text', e.target.value)}
                                                placeholder="Hindi blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.hindi.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.marathi && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Marathi Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.marathi.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('marathi', 'blessing_text', e.target.value)}
                                                placeholder="Marathi blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.marathi.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                                {selectedLanguages.gujarati && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                                Gujarati Blessing Text <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={languageData.gujarati.blessing_text}
                                                onChange={(e) => handleLanguageInputChange('gujarati', 'blessing_text', e.target.value)}
                                                placeholder="Gujarati blessing text"
                                                maxLength={50}
                                                style={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                                {languageData.gujarati.blessing_text.length}/50
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        )}

                        {/* Select Languages, Category and Status in One Row */}
                        <Row className="mb-3" style={{ alignItems: 'flex-start' }}>
                            <Col md={6}>
                                <Form.Label style={{ color: '#4A5568', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                                    Select Languages <span className="text-danger">*</span>
                                </Form.Label>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="lang-english"
                                        label="English"
                                        checked={selectedLanguages.english}
                                        onChange={() => handleLanguageChange('english')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="lang-hindi"
                                        label="Hindi"
                                        checked={selectedLanguages.hindi}
                                        onChange={() => handleLanguageChange('hindi')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="lang-marathi"
                                        label="Marathi"
                                        checked={selectedLanguages.marathi}
                                        onChange={() => handleLanguageChange('marathi')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="lang-gujarati"
                                        label="Gujarati"
                                        checked={selectedLanguages.gujarati}
                                        onChange={() => handleLanguageChange('gujarati')}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Category <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <option value="" disabled>Select category</option>
                                        {categoryOptions.map((option, index) => (
                                            <option key={index} value={option}>{option}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Status <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <option value="" disabled>Select status</option>
                                        {statusOptions.map((option, index) => (
                                            <option key={index} value={option}>{option}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* All Banners in One Row */}
                        <Row className="mb-3">
                            {selectedLanguages.english && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            English Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('english-image-upload').click()}
                                        >
                                            {languageData.english.imagePreview ? (
                                                <img
                                                    src={languageData.english.imagePreview}
                                                    alt="Preview"
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="english-image-upload"
                                            onChange={(e) => handleLanguageImageChange('english', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.hindi && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Hindi Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('hindi-image-upload').click()}
                                        >
                                            {languageData.hindi.imagePreview ? (
                                                <img
                                                    src={languageData.hindi.imagePreview}
                                                    alt="Preview"
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="hindi-image-upload"
                                            onChange={(e) => handleLanguageImageChange('hindi', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.marathi && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Marathi Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('marathi-image-upload').click()}
                                        >
                                            {languageData.marathi.imagePreview ? (
                                                <img
                                                    src={languageData.marathi.imagePreview}
                                                    alt="Preview"
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="marathi-image-upload"
                                            onChange={(e) => handleLanguageImageChange('marathi', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                            {selectedLanguages.gujarati && (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label style={{ color: '#4A5568', fontWeight: '500', fontSize: '14px' }}>
                                            Gujarati Banner <span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{
                                            border: '2px dashed #CBD5E0',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            textAlign: 'center',
                                            backgroundColor: '#FFFFFF',
                                            cursor: 'pointer',
                                            minHeight: '120px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                            onClick={() => document.getElementById('gujarati-image-upload').click()}
                                        >
                                            {languageData.gujarati.imagePreview ? (
                                                <img
                                                    src={languageData.gujarati.imagePreview}
                                                    alt="Preview"
                                                    style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '4px' }}
                                                />
                                            ) : (
                                                <>
                                                    <div style={{ fontSize: '32px', color: '#CBD5E0', marginBottom: '8px' }}>+</div>
                                                    <p style={{ color: '#718096', margin: 0, fontSize: '12px' }}>Upload</p>
                                                </>
                                            )}
                                        </div>
                                        <Form.Control
                                            type="file"
                                            id="gujarati-image-upload"
                                            onChange={(e) => handleLanguageImageChange('gujarati', e)}
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            style={{ display: 'none' }}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>

                        {/* Display Section in One Row */}
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label style={{ color: '#4A5568', fontWeight: '600', fontSize: '16px', marginBottom: '8px' }}>
                                    Display Section <span className="text-danger">*</span>
                                </Form.Label>
                                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                    <Form.Check
                                        type="checkbox"
                                        id="section-home"
                                        label="Home"
                                        checked={displaySections.home}
                                        onChange={() => handleDisplaySectionChange('home')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="section-wisdom"
                                        label="Wisdom"
                                        checked={displaySections.wisdom}
                                        onChange={() => handleDisplaySectionChange('wisdom')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="section-parikrama"
                                        label="Parikrama"
                                        checked={displaySections.parikrama}
                                        onChange={() => handleDisplaySectionChange('parikrama')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="section-teertha"
                                        label="Teertha"
                                        checked={displaySections.teertha}
                                        onChange={() => handleDisplaySectionChange('teertha')}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        id="section-devotion"
                                        label="Devotion"
                                        checked={displaySections.devotion}
                                        onChange={() => handleDisplaySectionChange('devotion')}
                                        style={{ fontSize: '14px' }}
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Action Link and Priority Row */}
                        <Row className="mb-3">
                            <Col md={9}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Action link/URL <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="action_link"
                                        value={formData.action_link}
                                        onChange={handleInputChange}
                                        placeholder="Enter Action link/URL"
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Priority <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        placeholder="Enter Priority"
                                        min="1"
                                        max="10"
                                        style={{
                                            height: '46px',
                                            border: '1px solid #E2E8F0',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Date Fields Row */}
                        <Row className="mb-3">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Display Start Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type="date"
                                            name="display_start_date"
                                            value={formData.display_start_date}
                                            onChange={handleInputChange}
                                            min={new Date().toISOString().slice(0, 10)}
                                            style={{
                                                height: '46px',
                                                border: '1px solid #E2E8F0',
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: '6px',
                                            }}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                        Display End Date
                                    </Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type="date"
                                            name="display_end_date"
                                            value={formData.display_end_date}
                                            onChange={handleInputChange}
                                            style={{
                                                height: '46px',
                                                border: '1px solid #E2E8F0',
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: '6px',
                                            }}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                            {formData.category === 'Live Aarti' ? (
                                <>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                                Time <span className="text-danger">*</span>
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="time"
                                                    name="display_time"
                                                    value={formData.display_time}
                                                    onChange={handleInputChange}
                                                    style={{
                                                        height: '46px',
                                                        border: '1px solid #E2E8F0',
                                                        backgroundColor: '#FFFFFF',
                                                        borderRadius: '6px',
                                                    }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                                Frequency <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="frequency"
                                                value={formData.frequency}
                                                onChange={handleInputChange}
                                                style={{
                                                    height: '46px',
                                                    border: '1px solid #E2E8F0',
                                                    backgroundColor: '#FFFFFF',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <option value="" disabled>Select frequency</option>
                                                {frequencyOptions.map((option, index) => (
                                                    <option key={index} value={option}>{option}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </>
                            ) : (
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={{ color: '#4A5568', fontWeight: '500', fontSize: '16px' }}>
                                            Frequency <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Select
                                            name="frequency"
                                            value={formData.frequency}
                                            onChange={handleInputChange}
                                            style={{
                                                height: '46px',
                                                border: '1px solid #E2E8F0',
                                                backgroundColor: '#FFFFFF',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            <option value="" disabled>Select frequency</option>
                                            {frequencyOptions.map((option, index) => (
                                                <option key={index} value={option}>{option}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </Form>
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || isSubmitted}
                        style={{
                            backgroundColor: '#5662F6',
                            borderColor: '#5662F6',
                            borderRadius: '6px',
                            padding: '10px 40px',
                            fontWeight: '500',
                            fontSize: '16px'
                        }}
                    >
                        {isSubmitting ? 'Creating...' : 'Create'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateMultiyatraBelt;
